
import React from "react";
import { animated, useTransition } from "@react-spring/web";

import SwitchButton from "src/components/switchButton";

import cx from "classnames";
import styles from "./styles.module.scss";

interface Option {
  key: string;
  label: string;
  selected?: boolean;
};

interface ExpandableToggleProps {
  title: string;
  description: string;
  value: boolean;
  onChange: (checked: boolean) => void;
  id: string;
  options?: Option[];
  onOptionChange?: (option: Option) => void;
  helpText?: React.ReactNode;
  collapsible: boolean;
  disabled?: boolean; 
}

export const ExpandableToggle: React.FC<ExpandableToggleProps> = ({
  title,
  description,
  value,
  onChange,
  id,
  options = [],
  onOptionChange,
  helpText,
  collapsible,
  disabled
}) => {

  const showCollapse = value && !!(options && onOptionChange);

  const [mounted, setMounted] = React.useState(false);
  const collapsedHeight = 0;
  const expandedHeight = 85;

  const handleOptionToggle = (option: Option) => {
    if (disabled) return;
    onOptionChange?.(option);
  };

  const handleToggleChange = () => {
    if (disabled) return;
    onChange(!value);
  };

  const transitions = useTransition(showCollapse, {
    from: { height: collapsedHeight },
    enter: { height: expandedHeight },
    leave: { height: collapsedHeight },
    config: { tension: 150, friction: 30 },
    immediate: !mounted,
  });

  const optionsContent = (
    <div
      className={cx(
        styles.optionsContainer,
        { [styles.withHelpText]: helpText },
        { [styles.noHelpText]: !helpText }
      )}
    >
      <div className={styles.optionsRow}>
        {options.map(option => (
          <div
            key={option.key}
            className={cx(styles.option, { [styles.selected]: option.selected, [styles.disabled]: disabled })}
            onClick={() => handleOptionToggle(option)}
          >
            <i
              className={cx(
                option.selected
                  ? "fa-solid fa-check-circle"
                  : "paperflite-radio-button-unselected"
              )}
              aria-hidden="true"
            />
            <label>{option.label}</label>
          </div>
        ))}
      </div>
      {helpText && <div className={styles.helpText}>{helpText}</div>}
    </div>
  );

  React.useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <div
      className={cx(styles.optionContainer, { [styles.expanded]: showCollapse || collapsible })}
    >
      <div className={styles.switchContainer} onClick={() => onChange(!value)}>
        <div className={cx(styles.textContainer, {[styles.disabled]: disabled})}>
          <div className={styles.title}>{title}</div>
          {description && <div className={styles.description}>{description}</div>}
        </div>
        <div onClick={e => e.stopPropagation()}>
          <SwitchButton
            checked={value}
            handleCheck={handleToggleChange}
            id={id}
            className={cx(styles.switch, { [styles.active]: value, [styles.disabled]: disabled })}
            disabled={disabled}
          />
        </div>
      </div>

      {collapsible && (
        <div className={cx(styles.collapseContent, styles.expanded)}>
          {optionsContent}
        </div>
      )}

      {!collapsible &&
        transitions((style, item) =>
          item && (
            <animated.div
              style={{ ...style, overflow: "hidden" }}
              className={cx(styles.collapseContent, { [styles.expanded]: showCollapse })}
            >
              {optionsContent}
            </animated.div>
          )
        )}
    </div>
  );
};